import { useState, useEffect } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import {
  getNotificationData,
  putNotificationData,
} from "@services/API/Requests/NotificationRequests";
import { NOTIFICATION } from "@types/notification";
import LoadingIcon from "@common/LoadingIcon";
import { TranslateErrorMessage } from "@services/ErrorHandling";
import { useSelector } from "react-redux";
import messages from "@constants/Messages";
import { error_message } from "@constants/Errors";
import { NOTIFICATION_PATHS } from "@constants/NavigationPaths";
import { validateDates } from "@pages/Notification/common/utilities/ValidateDates";

export const useNotificationPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { id } = useParams();

  const broadcastToast = () => toast.success(messages.notification.restart);
  const stopToast = () => toast.success(messages.notification.stop);

  const startModalTitle: string = "再配信注意";
  const startModalBody: string = "お知らせを再配信します。よろしいですか？";
  const startModalConfirm: string = "再配信する";

  const pauseModalTitle: string = "停止注意";
  const pauseModalBody: string = "お知らせを停止します。よろしいですか？";
  const pauseModalConfirm: string = "停止する";

  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const [notification, setNotification] = useState<NOTIFICATION | undefined>();
  const [pauseModalOpen, setPauseModalOpen] = useState<boolean>(false);
  const [startModalOpen, setStartModalOpen] = useState<boolean>(false);
  const [status, setStatus] = useState<boolean>(false);
  const [isEditing, setIsEditing] = useState<boolean>(true);

  const errorToast = (response) => toast.error(`${response}`);
  const successToast = (response) => toast.success(`${response}`);

  const isAdminLoggedIn: boolean = useSelector((state) => state.auth.isAdmin);

  const { viewOnly } = location.state || !isAdminLoggedIn;

  useEffect(() => {
    setLoading(true);
    getNotificationData(setNotification, setLoading, setError, id);
  }, [id]);

  const handleStopClick = () => {
    setPauseModalOpen(true);
  };

  const handleStopCloseModal = () => {
    setPauseModalOpen(false);
  };

  const handleStopConfirm = () => {
    setPauseModalOpen(false);
    stopToast();
    setStatus(false);
    if (notification) {
      notification.status = !notification?.status;
    } else {
      console.error(error_message.notification.is_null);
    }
  };

  const handleCloseStartModal = () => {
    setStartModalOpen(false);
  };

  const handleStartClick = () => {
    setStartModalOpen(true);
  };

  const handleStartConfirm = () => {
    setStartModalOpen(false);
    broadcastToast();
    setStatus(true);
    if (notification) {
      notification.status = !notification.status;
    } else {
      console.error(error_message.notification.is_null);
    }
  };

  const toggleEditMode = () => {
    setIsEditing((prev) => !prev);
  };

  const handleCancelButtonClick = () => {
    if (isEditing) {
      navigate(NOTIFICATION_PATHS.main);
    } else {
      toggleEditMode();
    }
  };

  const handleUpdateButtonClick = () => {
    if (!isEditing) {
      let response;

      const validDates: boolean = validateDates(errorToast, notification);

      if (!validDates) {
        return;
      }

      putNotificationData(notification, setLoading, setError, notification?.id)
        .then((response) => {
          if (response.status == 200) {
            successToast(response.message);
          } else {
            const errorMessage = TranslateErrorMessage(response.message);
            console.error(errorMessage);
            setError(errorMessage);
            errorToast(errorMessage);
          }
        })
        .catch((error) => {
          const errorMessage = TranslateErrorMessage(response.message);
          console.error(errorMessage);
          setError(errorMessage);
          errorToast(errorMessage);
        });
      setTimeout(() => {
        navigate(NOTIFICATION_PATHS.main);
      }, 3000);
    } else {
      toggleEditMode();
    }
  };

  if (loading) {
    return (
      <div>
        <LoadingIcon></LoadingIcon>
      </div>
    );
  }

  if (notification?.status == false && !isAdminLoggedIn) {
    return (
      <div>
        <p className="not-found-text">
          {error_message.notification.not_found(id)}
        </p>
      </div>
    );
  }

  return {
    notification,
    viewOnly,
    isAdminLoggedIn,
    isEditing,
    startModalTitle,
    startModalBody,
    startModalConfirm,
    pauseModalTitle,
    pauseModalOpen,
    pauseModalBody,
    pauseModalConfirm,
    startModalOpen,
    setNotification,
    handleStartClick,
    handleStopClick,
    handleCancelButtonClick,
    handleUpdateButtonClick,
    handleStopCloseModal,
    handleStopConfirm,
    handleCloseStartModal,
    handleStartConfirm,
  };
};
